import React, { useEffect, useRef, useState } from "react";

import { FIELD_MAX, draftFromRow, emptyCrossroads, validateCrossroads } from "./quizHelpers";
import { readableTextOn } from "../../../utils/crossroadsTheme";

function Counter({ value, max }) {
  const n = String(value || "").length;
  return <span className={`cq-count${n > max ? " is-over" : ""}`}>{n}/{max}</span>;
}

/** Add / edit one crossroads (PRD 4.2 / 5.3). */
export default function QuizQuestionModal({ open, mode, initial, onClose, onSubmit, isSaving, themeColors, hasCorrectAnswers = true }) {
  const [draft, setDraft] = useState(emptyCrossroads);
  const [error, setError] = useState({ field: "", message: "" });
  const bodyRef = useRef(null);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setDraft(isEdit && initial ? draftFromRow(initial) : emptyCrossroads());
    setError({ field: "", message: "" });
  }, [open, isEdit, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !isSaving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isSaving, onClose]);

  if (!open) return null;

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setDraft((d) => ({ ...d, [key]: FIELD_MAX[key] ? String(value).slice(0, FIELD_MAX[key]) : value }));
    if (error.field) setError({ field: "", message: "" });
  };

  // Game Settings → General → "Crossroads have a correct answer" decides this for every crossroads.
  const scored = hasCorrectAnswers;
  const themeA = themeColors?.pathA || "#2E86DE";
  const themeB = themeColors?.pathB || "#E67E22";
  const colorA = draft.path_color_mode === "custom" ? draft.path_a_color || themeA : themeA;
  const colorB = draft.path_color_mode === "custom" ? draft.path_b_color || themeB : themeB;

  const setColorMode = (modeValue) => {
    setDraft((d) => ({
      ...d,
      path_color_mode: modeValue,
      // Seed custom pickers with the theme colours so they start somewhere sensible.
      path_a_color: modeValue === "custom" && !d.path_a_color ? themeA : d.path_a_color,
      path_b_color: modeValue === "custom" && !d.path_b_color ? themeB : d.path_b_color,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const result = validateCrossroads(draft, { hasCorrectAnswers });
    if (!result.ok) {
      setError({ field: result.field, message: result.message });
      const el = bodyRef.current?.querySelector(`[data-field="${result.field}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus?.();
      return;
    }
    try {
      await onSubmit({ question: result.data });
    } catch {
      /* parent shows the error */
    }
  };

  const invalid = (field) => (error.field === field ? " is-invalid" : "");
  const textInput = (key, props = {}) => (
    <input
      id={`cq-${key}`}
      data-field={key}
      className={`cq-input${invalid(key)}`}
      value={draft[key]}
      onChange={set(key)}
      maxLength={FIELD_MAX[key]}
      disabled={isSaving}
      {...props}
    />
  );
  const textArea = (key, rows, props = {}) => (
    <textarea
      id={`cq-${key}`}
      data-field={key}
      className={`cq-input${invalid(key)}`}
      rows={rows}
      value={draft[key]}
      onChange={set(key)}
      maxLength={FIELD_MAX[key]}
      disabled={isSaving}
      {...props}
    />
  );
  const label = (key, text, required) => (
    <div className="cq-label-row">
      <label htmlFor={`cq-${key}`}>
        {text}
        {required ? <span className="cq-req"> *</span> : null}
      </label>
      {FIELD_MAX[key] && FIELD_MAX[key] > 10 ? <Counter value={draft[key]} max={FIELD_MAX[key]} /> : null}
    </div>
  );

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={() => !isSaving && onClose()}>
      <form
        className="quiz-modal cq-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        noValidate
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Crossroads" : "Add Crossroads"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close" disabled={isSaving}>
            ×
          </button>
        </div>

        <div className="quiz-modal-body cq-body" ref={bodyRef}>
          {error.message ? (
            <p className="cr-alert cr-alert-error quiz-modal-error" role="alert">
              {error.message}
            </p>
          ) : null}

          <section className="cq-section">
            <h3>Scenario</h3>
            {label("question_name", "Scenario description", true)}
            {textArea("question_name", 4, { placeholder: "Describe the situation the player faces at this crossroads." })}
          </section>

          <section className="cq-section">
            <h3>Decision paths</h3>
            <div className="cq-grid">
              <div>
                {label("option_one", "Option A (Path A)", true)}
                {textInput("option_one", { placeholder: "First choice" })}
              </div>
              <div>
                {label("option_two", "Option B (Path B)", true)}
                {textInput("option_two", { placeholder: "Second choice" })}
              </div>
            </div>
            {scored ? (
            <fieldset className="cq-correct" data-field="correct" tabIndex={-1}>
              <legend>
                Correct answer<span className="cq-req"> *</span>
              </legend>
              <div className="cq-grid">
                {[
                  ["A", draft.option_one, colorA],
                  ["B", draft.option_two, colorB],
                ].map(([letter, text, bg]) => (
                  <label key={letter} className={`cq-correct-option${draft.correct === letter ? " is-checked" : ""}${invalid("correct")}`}>
                    <input
                      type="radio"
                      name="cq-correct"
                      value={letter}
                      checked={draft.correct === letter}
                      onChange={() => set("correct")(letter)}
                      disabled={isSaving}
                    />
                    <span className="cq-letter" style={{ background: bg, color: readableTextOn(bg) }}>
                      {letter}
                    </span>
                    <span className="cq-correct-text">{text || `Option ${letter}`}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            ) : null}
          </section>

          <section className="cq-section">
            <h3>Path colours</h3>
            <div className="cq-segment" role="radiogroup" aria-label="Path colours">
              {[
                ["theme", "Theme colours"],
                ["random", "Random colours"],
                ["custom", "Custom colours"],
              ].map(([value, text]) => (
                <label key={value} className={draft.path_color_mode === value ? "is-checked" : ""}>
                  <input
                    type="radio"
                    name="cq-color-mode"
                    value={value}
                    checked={draft.path_color_mode === value}
                    onChange={() => setColorMode(value)}
                    disabled={isSaving}
                  />
                  {text}
                </label>
              ))}
            </div>
            {draft.path_color_mode === "random" ? (
              <p className="cq-hint">A colour pair is picked automatically — the same on the main screen and every phone.</p>
            ) : null}
            {draft.path_color_mode === "theme" ? (
              <p className="cq-hint">Uses the Road A / Road B colours from Game Settings → Branding & Theme.</p>
            ) : null}
            {draft.path_color_mode === "custom" ? (
              <div className="cq-grid">
                {[
                  ["path_a_color", "Path A colour"],
                  ["path_b_color", "Path B colour"],
                ].map(([key, text]) => (
                  <div key={key} className="cq-color">
                    <input
                      type="color"
                      aria-label={text}
                      value={/^#[0-9a-f]{6}$/i.test(draft[key]) ? draft[key] : "#000000"}
                      onChange={set(key)}
                      disabled={isSaving}
                    />
                    <div>
                      <label htmlFor={`cq-${key}`}>{text}</label>
                      {textInput(key, { className: `cq-input cq-hex${invalid(key)}` })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="cq-section">
            <h3>Learning — “Did You Know?”</h3>
            {label("dyk_heading", "Heading")}
            {textInput("dyk_heading", { placeholder: "Did You Know?" })}
            {label("more_info", "Learning content", true)}
            {textArea("more_info", 3, { placeholder: "The key takeaway behind this scenario." })}
          </section>

          {scored ? (
            <section className="cq-section">
              <div className="cq-points">
                {label("points", "Points for the correct answer")}
                {textInput("points", { type: "number", min: 0, max: 1000, inputMode: "numeric" })}
              </div>
            </section>
          ) : null}
        </div>

        <div className="quiz-modal-footer">
          <button type="submit" className="quiz-modal-btn quiz-modal-btn-save" disabled={isSaving}>
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isSaving ? "Saving…" : isEdit ? "Update Crossroads" : "Add Crossroads"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose} disabled={isSaving}>
            Close
          </button>
        </div>
      </form>
    </div>
  );
}
